import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Monitor as MonitorIcon,
  Assessment as AssessmentIcon,
  ViewTimeline as TemplatesIcon,
} from '@mui/icons-material';
import { TabType, Shift, Driver, Assignment, AssignmentStatus, ShiftStatus, RecurrenceType, TemplateStatus, ShiftTemplate } from './types';
import { FETCH_ALL_SHIFTS, FETCH_SHIFT, ACTIVATE_SHIFT, CANCEL_SHIFT, BULK_ASSIGN_COURIERS_TO_SHIFT, UNASSIGN_COURIER_FROM_SHIFT, DELETE_SHIFT_TEMPLATE, UPDATE_SHIFT_TEMPLATE, FETCH_ALL_SHIFT_TEMPLATES, FETCH_ALL_COURIERS } from '../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../constants/apollo';
import { DriverAssignmentView } from './components/DriverAssignmentView';
import { LiveMonitorView } from './components/LiveMonitorView';
import { ComplianceReportsView } from './components/ComplianceReportsView';
import { ShiftsView } from './components/ShiftsView';
import { ShiftTemplatesView } from './components/ShiftTemplatesView';
import ShiftManagementDocs from '../../components/ShiftManagementDocs';

const VIEW_MODES = new Set(['card', 'table']);
const DEFAULT_VIEW_MODE = 'card';
const ITEMS_PER_PAGE = 12;

const STORAGE_KEYS = {
  page: 'shiftManagement:page',
  viewMode: 'shiftManagement:view',
  activeTab: 'shiftManagement:activeTab',
  templatePage: 'shiftManagement:templatePage',
  templateViewMode: 'shiftManagement:templateView',
  selectedShiftId: 'shiftManagement:selectedShiftId',
  selectedDriverIds: 'shiftManagement:selectedDriverIds',
  isReportsMounted: 'shiftManagement:isReportsMounted',
};

const isBrowser = typeof window !== 'undefined';

const storage = {
  get: (key: string) => {
    if (!isBrowser) return null;
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },
  set: (key: string, value: string) => {
    if (!isBrowser) return;
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      return;
    }
  },
};

const getStoredPage = () => {
  const raw = storage.get(STORAGE_KEYS.page);
  if (raw === null) return null;
  const parsed = Number(raw);
  if (Number.isNaN(parsed) || parsed < 0) return null;
  return Math.floor(parsed);
};

const getStoredViewMode = (): 'card' | 'table' | null => {
  const raw = storage.get(STORAGE_KEYS.viewMode);
  return raw && VIEW_MODES.has(raw) ? (raw as 'card' | 'table') : null;
};

const ShiftManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [docsOpen, setDocsOpen] = useState(false);

  const getInitialTab = (): TabType => {
    const tabParam = searchParams.get('tab');
    const validTabs: TabType[] = ['shifts', 'assignments', 'monitor', 'reports', 'templates'];
    if (tabParam && validTabs.includes(tabParam as TabType)) {
      return tabParam as TabType;
    }

    const storedTab = storage.get(STORAGE_KEYS.activeTab);
    if (storedTab && validTabs.includes(storedTab as TabType)) {
      return storedTab as TabType;
    }

    return 'shifts';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());

  const getInitialReportsMounted = (): boolean => {
    const stored = storage.get(STORAGE_KEYS.isReportsMounted);
    if (stored === 'true') return true;
    const initialTab = getInitialTab();
    if (initialTab === 'reports') {
      return true;
    }
    return false;
  };

  const [isReportsMounted, setIsReportsMounted] = useState<boolean>(getInitialReportsMounted);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const storedTab = storage.get(STORAGE_KEYS.activeTab);
    const currentTab = tabParam || storedTab || 'shifts';

    if (!tabParam) {
      setSearchParams({ tab: currentTab }, { replace: true });
    }
    if (!storedTab) {
      storage.set(STORAGE_KEYS.activeTab, currentTab);
    }

    const wasReportsMounted = storage.get(STORAGE_KEYS.isReportsMounted);
    if (wasReportsMounted === 'true') {
      setIsReportsMounted(true);
    } else if (currentTab === 'reports') {
      setIsReportsMounted(true);
      storage.set(STORAGE_KEYS.isReportsMounted, 'true');
    }
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const validTabs: TabType[] = ['shifts', 'assignments', 'monitor', 'reports', 'templates'];
    if (tabParam && validTabs.includes(tabParam as TabType)) {
      const tabFromParams = tabParam as TabType;
      if (tabFromParams !== activeTab) {
        setActiveTab(tabFromParams);
        storage.set(STORAGE_KEYS.activeTab, tabFromParams);
      }
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    if (activeTab === 'reports') {
      setIsReportsMounted(true);
      storage.set(STORAGE_KEYS.isReportsMounted, 'true');
    }
  }, [activeTab]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: false });
    storage.set(STORAGE_KEYS.activeTab, tab);
  };

  const [shiftFilters, setShiftFilters] = useState({
    cityId: undefined as number | undefined,
    neighborhoodId: undefined as number | undefined,
    status: undefined as ShiftStatus | undefined,
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
    search: undefined as string | undefined,
    order: 'desc' as string,
    shiftTemplateId: undefined as string | undefined,
    activeNow: undefined as boolean | undefined,
  });

  const [viewMode, setViewMode] = useState<'card' | 'table'>(() => {
    const viewFromStorage = getStoredViewMode();
    return viewFromStorage || DEFAULT_VIEW_MODE;
  });

  const [page, setPage] = useState(() => {
    const pageFromStorage = getStoredPage();
    return pageFromStorage !== null ? pageFromStorage : 0;
  });

  const [templateViewMode, setTemplateViewMode] = useState<'card' | 'table'>(() => {
    const viewFromStorage = storage.get(STORAGE_KEYS.templateViewMode);
    return (viewFromStorage && VIEW_MODES.has(viewFromStorage)) ? (viewFromStorage as 'card' | 'table') : DEFAULT_VIEW_MODE;
  });

  const [templatePage, setTemplatePage] = useState(() => {
    const pageFromStorage = storage.get(STORAGE_KEYS.templatePage);
    return pageFromStorage !== null ? parseInt(pageFromStorage, 10) : 0;
  });

  const { data: shiftsData, loading: shiftsLoading, refetch: refetchShifts } = useQuery(FETCH_ALL_SHIFTS, {
    ...stagingOptions,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: page * ITEMS_PER_PAGE,
      cityId: shiftFilters.cityId,
      neighborhoodId: shiftFilters.neighborhoodId,
      status: shiftFilters.status ? shiftFilters.status.toUpperCase() : undefined,
      startDate: shiftFilters.startDate || undefined,
      endDate: shiftFilters.endDate || undefined,
      order: shiftFilters.order || 'desc',
      search: shiftFilters.search || undefined,
      shiftTemplateId: shiftFilters.shiftTemplateId || undefined,
      activeNow: shiftFilters.activeNow !== undefined ? shiftFilters.activeNow : undefined,
    },
    fetchPolicy: 'network-only',
  });

  const shifts: Shift[] = useMemo(() => {
    if (!shiftsData?.fetchAllShifts?.items) {
      return [];
    }

    return shiftsData.fetchAllShifts.items.map((item: any) => ({
      id: item.id,
      name: item.name || `Shift ${item.id}`,
      startTime: item.startTime,
      endTime: item.endTime,
      requiredDrivers: item.requiredDrivers || 1,
      maxDrivers: item.requiredDrivers || 1,
      status: (item.status?.toLowerCase() || 'draft') as ShiftStatus,
      cityId: item.city?.id || '',
      cityName: item.city?.name || undefined,
      neighborhoodId: item.neighborhood?.id || undefined,
      neighborhoodName: item.neighborhood?.name || undefined,
      zone: item.neighborhood?.name || undefined,
      assignedDriversCount: item.assignedDriversCount || 0,
      availableSlots: item.availableSlots || (item.requiredDrivers || 1),
      startLatitude: undefined,
      startLongitude: undefined,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      shiftTemplateId: item.shiftTemplate?.id,
      shiftTemplateName: item.shiftTemplate?.name,
      shiftTemplate: item.shiftTemplate || undefined,
    }));
  }, [shiftsData]);

  const DRIVER_LIMIT = 50;

  const [driverSearchQuery, setDriverSearchQuery] = useState<string>('');

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(driverSearchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [driverSearchQuery]);

  const { data: driversData, loading: driversLoading, fetchMore: fetchMoreDrivers, refetch: refetchDrivers } = useQuery(FETCH_ALL_COURIERS, {
    ...stagingOptions,
    variables: {
      limit: DRIVER_LIMIT,
      offset: 0,
      query: debouncedSearchQuery || undefined,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const totalDriversCount = driversData?.fetchAllCouriers?.totalCount || 0;
  const hasMoreDrivers = (driversData?.fetchAllCouriers?.items?.length || 0) < totalDriversCount;

  const isFetchingMoreRef = useRef(false);

  const loadMoreDrivers = async () => {
    if (driversLoading || !hasMoreDrivers || isFetchingMoreRef.current) return;

    try {
      isFetchingMoreRef.current = true;
      await fetchMoreDrivers({
        variables: {
          offset: driversData?.fetchAllCouriers?.items?.length || 0,
          limit: DRIVER_LIMIT,
          query: debouncedSearchQuery || undefined,
        },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult?.fetchAllCouriers) {
            isFetchingMoreRef.current = false;
            return prev;
          }

          const existingIds = new Set(prev.fetchAllCouriers.items.map((item: any) => item.id));
          const newItems = fetchMoreResult.fetchAllCouriers.items.filter(
            (item: any) => !existingIds.has(item.id)
          );

          isFetchingMoreRef.current = false;
          return {
            fetchAllCouriers: {
              ...fetchMoreResult.fetchAllCouriers,
              items: [
                ...prev.fetchAllCouriers.items,
                ...newItems,
              ],
            },
          };
        },
      });
    } catch (error) {
      console.error('Failed to load more drivers', error);
      isFetchingMoreRef.current = false;
    }
  };

  const drivers: Driver[] = useMemo(() => {
    if (!driversData?.fetchAllCouriers?.items) {
      return [];
    }

    return driversData.fetchAllCouriers.items.map((item: any) => ({
      id: item.id,
      firstName: item.firstName || '',
      lastName: item.lastName || '',
      name: item.firstName && item.lastName
        ? `${item.firstName} ${item.lastName}`
        : item.firstName || item.lastName || 'Unknown',
      mobileNumber: item.mobileNumber || '',
      phone: item.mobileNumber || '',
      status: item.isOnline
        ? (item.isBusy ? 'Busy' : 'Online')
        : 'Idle' as Driver['status'],
      latitude: item.latitude,
      longitude: item.longitude,
      avatarUrl: item.avatarUrl,
      rating: item.rating,
      vehicleType: item.vehicleType,
      isBanned: item.isBanned,
      lastSeen: item.lastSeen,
    }));
  }, [driversData]);

  const [assignments, setAssignments] = useState<Assignment[]>([]);

  const [templateFilters, setTemplateFilters] = useState({
    cityId: undefined as number | undefined,
    status: undefined as string | undefined,
    recurrenceType: undefined as string | undefined,
    search: undefined as string | undefined,
  });

  const { data: templatesData, loading: templatesLoading, refetch: refetchTemplates } = useQuery(FETCH_ALL_SHIFT_TEMPLATES, {
    ...stagingOptions,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: templatePage * ITEMS_PER_PAGE,
      cityId: templateFilters.cityId,
      status: templateFilters.status ? templateFilters.status.toUpperCase() : undefined,
      recurrenceType: templateFilters.recurrenceType ? templateFilters.recurrenceType.toUpperCase() : undefined,
      search: templateFilters.search || undefined,
    },
    fetchPolicy: 'network-only',
  });

  const templates = useMemo(() => {
    if (!templatesData?.fetchAllShiftTemplates?.items) {
      return [];
    }

    return templatesData.fetchAllShiftTemplates.items.map((item: any) => ({
      id: item.id,
      name: item.name,
      startTime: item.startTime,
      endTime: item.endTime,
      requiredDrivers: item.requiredDrivers || 1,
      cityId: item.city?.id || undefined,
      neighborhoodId: item.neighborhood?.id || undefined,
      recurrenceType: (item.recurrenceType?.toLowerCase() || 'daily') as RecurrenceType,
      daysOfWeek: item.daysOfWeek || [],
      daysOfWeekNames: item.daysOfWeekNames || [],
      status: (item.status?.toLowerCase() || 'active') as TemplateStatus,
      courierIds: item.couriers?.map((courier: any) => courier.id) || [],
      specificDate: item.specificDate || undefined,
      validFrom: item.validFrom || undefined,
      validUntil: item.validUntil || undefined,
      excludeDates: [],
      priority: undefined,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    }));
  }, [templatesData]);

  useEffect(() => {
    if ((location.state as { refresh?: boolean })?.refresh) {
      refetchShifts();
      refetchTemplates();
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, refetchShifts, refetchTemplates]);

  // Restore Driver Assignment state from storage
  const getStoredSelectedShiftId = (): string => {
    const raw = storage.get(STORAGE_KEYS.selectedShiftId);
    return raw || '';
  };

  const getStoredSelectedDriverIds = (): string[] => {
    const raw = storage.get(STORAGE_KEYS.selectedDriverIds);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const [selectedShiftId, setSelectedShiftId] = useState<string>(getStoredSelectedShiftId);
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>(getStoredSelectedDriverIds);

  const { refetch: refetchSelectedShift } = useQuery(FETCH_SHIFT, {
    ...stagingOptions,
    variables: { id: selectedShiftId || '' },
    skip: !selectedShiftId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.fetchShift?.shiftAssignments) {
        const shiftAssignments: Assignment[] = data.fetchShift.shiftAssignments.map((assignment: any) => ({
          id: assignment.id,
          shiftId: selectedShiftId,
          driverId: assignment.courier?.id || '',
          courierId: assignment.courier?.id || '',
          status: (assignment.status?.toLowerCase() || 'pending') as AssignmentStatus,
          createdAt: undefined,
          updatedAt: undefined,
          assignedAt: undefined,
          checkedInAt: assignment.checkedInAt || undefined,
          courier: assignment.courier ? {
            id: assignment.courier.id,
            firstName: assignment.courier.firstName,
            lastName: assignment.courier.lastName,
            mobileNumber: assignment.courier.mobileNumber,
          } : undefined,
        }));
        setAssignments(shiftAssignments);
      } else {
        setAssignments([]);
      }
    },
  });

  const totalShiftsCount = shiftsData?.fetchAllShifts?.totalCount || shifts.length;

  const paginatedShifts = shifts;

  const totalPages = Math.ceil(totalShiftsCount / ITEMS_PER_PAGE);

  useEffect(() => {
    storage.set(STORAGE_KEYS.page, String(page));
  }, [page]);

  useEffect(() => {
    storage.set(STORAGE_KEYS.viewMode, viewMode);
  }, [viewMode]);

  useEffect(() => {
    storage.set(STORAGE_KEYS.templatePage, String(templatePage));
  }, [templatePage]);

  useEffect(() => {
    storage.set(STORAGE_KEYS.templateViewMode, templateViewMode);
  }, [templateViewMode]);

  // Persist Driver Assignment state
  useEffect(() => {
    if (selectedShiftId) {
      storage.set(STORAGE_KEYS.selectedShiftId, selectedShiftId);
    } else {
      storage.set(STORAGE_KEYS.selectedShiftId, '');
    }
  }, [selectedShiftId]);

  useEffect(() => {
    if (selectedDriverIds.length > 0) {
      storage.set(STORAGE_KEYS.selectedDriverIds, JSON.stringify(selectedDriverIds));
    } else {
      storage.set(STORAGE_KEYS.selectedDriverIds, JSON.stringify([]));
    }
  }, [selectedDriverIds]);

  // Reset template page when filters change
  useEffect(() => {
    setTemplatePage(0);
  }, [templateFilters.cityId, templateFilters.status, templateFilters.recurrenceType, templateFilters.search]);

  const tabs = [
    { id: 'templates' as TabType, label: 'Shift Scheduler', icon: TemplatesIcon },
    { id: 'shifts' as TabType, label: 'Shift Session', icon: ScheduleIcon },
    { id: 'assignments' as TabType, label: 'Driver Assignment', icon: AssignmentIcon },
    { id: 'monitor' as TabType, label: 'Live Monitor', icon: MonitorIcon },
    { id: 'reports' as TabType, label: 'Compliance Reports', icon: AssessmentIcon },
  ];

  const handleCreateShift = () => {
    navigate('/shift-management/create');
  };

  const [activateShiftMutation] = useMutation(ACTIVATE_SHIFT, {
    ...stagingOptions,
  });

  const [cancelShiftMutation] = useMutation(CANCEL_SHIFT, {
    ...stagingOptions,
  });

  const [bulkAssignMutation] = useMutation(BULK_ASSIGN_COURIERS_TO_SHIFT, {
    ...stagingOptions,
  });

  const [unassignMutation] = useMutation(UNASSIGN_COURIER_FROM_SHIFT, {
    ...stagingOptions,
    fetchPolicy: 'no-cache',
  });

  const [deleteTemplateMutation] = useMutation(DELETE_SHIFT_TEMPLATE, {
    ...stagingOptions,
  });

  const [updateTemplateMutation] = useMutation(UPDATE_SHIFT_TEMPLATE, {
    ...stagingOptions,
  });

  const handleToggleShift = async (shiftId: string) => {
    try {
      const shift = shifts.find(s => s.id === shiftId);
      if (!shift) {
        toast.error('Shift session not found');
        return;
      }

      if (shift.status === 'draft') {
        await activateShiftMutation({
          variables: { input: { id: shiftId } }
        });

        toast.success('Shift session activated successfully');
      } else if (shift.status === 'active') {
        if (!window.confirm('Are you sure you want to cancel this shift session? This will cancel all pending/confirmed assignments.')) {
          return;
        }

        await cancelShiftMutation({
          variables: { input: { id: shiftId } }
        });

        toast.success('Shift session cancelled successfully');
      } else {
        toast.error(`Cannot toggle shift session with status: ${shift.status}`);
        return;
      }

      await refetchShifts();
    } catch (error: any) {
      console.error('Failed to toggle shift status', error);
      toast.error(error.message || 'Failed to toggle shift session status. Please try again.');
    }
  };

  const isShiftEnabled = (shift: Shift) => {
    return shift.status === 'active';
  };

  const getAssignedDriversForShift = (shiftId: string) => {
    return assignments.filter((a) => a.shiftId === shiftId).map((a) => a.driverId);
  };

  const getAvailableDrivers = () => {
    return drivers;
  };

  const handleDriverToggle = (driverId: string) => {
    setSelectedDriverIds((prev) =>
      prev.includes(driverId)
        ? prev.filter((id) => id !== driverId)
        : [...prev, driverId]
    );
  };

  const handleAssignDrivers = async () => {
    if (!selectedShiftId || selectedDriverIds.length === 0) {
      toast.error('Please select a shift and at least one driver');
      return;
    }

    let selectedShift = shifts.find((s) => s.id === selectedShiftId);

    if (!selectedShift) {
      try {
        const { data } = await refetchSelectedShift();
        if (data?.fetchShift) {
          selectedShift = {
            id: data.fetchShift.id,
            name: data.fetchShift.name || `Shift ${data.fetchShift.id}`,
            startTime: data.fetchShift.startTime,
            endTime: data.fetchShift.endTime,
            requiredDrivers: data.fetchShift.requiredDrivers || 1,
            maxDrivers: data.fetchShift.requiredDrivers || 1,
            status: (data.fetchShift.status?.toLowerCase() || 'draft') as ShiftStatus,
            cityId: '',
            assignedDriversCount: data.fetchShift.shiftAssignments?.length || 0,
            availableSlots: (data.fetchShift.requiredDrivers || 1) - (data.fetchShift.shiftAssignments?.length || 0),
          };
        }
      } catch (error) {
        console.error('Error fetching shift:', error);
      }
    }

    if (!selectedShift) {
      toast.error('Shift session not found. Please try selecting the shift session again.');
      return;
    }

    const maxDrivers = selectedShift.requiredDrivers || selectedShift.maxDrivers || 0;
    // Count assigned drivers excluding cancelled assignments
    const currentAssigned = assignments.filter((a) => a.shiftId === selectedShiftId && a.status !== 'cancelled').length;
    if (currentAssigned + selectedDriverIds.length > maxDrivers) {
      toast.error(
        `Cannot assign more than ${maxDrivers} drivers to this shift session. Currently assigned: ${currentAssigned}`
      );
      return;
    }

    try {
      const response = await bulkAssignMutation({
        variables: {
          input: {
            shiftId: selectedShiftId,
            courierIds: selectedDriverIds,
          },
        },
      });

      if (response.data?.bulkAssignCouriersToShift?.errors && response.data.bulkAssignCouriersToShift.errors.length > 0) {
        toast.error(`Some assignments failed: ${response.data.bulkAssignCouriersToShift.errors.join(', ')}`);
      }

      const successfulCount = response.data?.bulkAssignCouriersToShift?.successfulAssignments || selectedDriverIds.length;
      toast.success(`Successfully assigned ${successfulCount} driver(s) to ${selectedShift.name}`);

      await refetchShifts();
      await refetchDrivers();
      if (selectedShiftId) {
        await refetchSelectedShift();
      }

      setSelectedDriverIds([]);
    } catch (error: any) {
      console.error('Failed to assign drivers', error);
      const errorMessage = error.graphQLErrors?.[0]?.message || error.networkError?.message || error.message || 'Failed to assign drivers. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleUnassignDriver = async (shiftId: string, driverId: string) => {
    const assignment = assignments.find(a => a.shiftId === shiftId && a.driverId === driverId);

    if (assignment && (assignment.status === 'checked_in' || assignment.status === 'completed')) {
      toast.error('Cannot unassign: Driver has checked in or completed the shift');
      return;
    }

    try {
      const response = await unassignMutation({
        variables: {
          input: {
            shiftId: shiftId,
            courierId: driverId,
          },
        },
      });

      if (response.data?.unassignCourierFromShift?.success) {
        toast.success('Successfully unassigned driver from shift');

        await refetchShifts();
        await refetchDrivers();

        if (selectedShiftId === shiftId) {
          await refetchSelectedShift();
        }
      } else {
        console.error('Unassign failed - response:', response);
        const errorMessage = response.errors?.[0]?.message || 'Failed to unassign driver';
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error('Failed to unassign driver', error);
      console.error('Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        fullError: error,
      });

      if (error.networkError) {
        const networkErr = error.networkError;
        if (networkErr.statusCode || networkErr.result) {
          const graphQLError = networkErr.result?.errors?.[0] || networkErr.result?.error;
          const errorMessage = graphQLError?.message || networkErr.message || 'Failed to unassign driver. Please try again.';
          toast.error(errorMessage);
        } else if (networkErr.message === 'Failed to fetch' || networkErr.name === 'TypeError') {
          toast.error('Cannot connect to the server. Please check if the GraphQL server is running and accessible.');
        } else {
          toast.error(networkErr.message || 'Network error occurred. Please try again.');
        }
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const errorMessage = error.graphQLErrors[0].message || 'Failed to unassign driver. Please try again.';
        toast.error(errorMessage);
      } else {
        const errorMessage = error.message || 'Failed to unassign driver. Please try again.';
        toast.error(errorMessage);
      }
    }
  };

  const handleUpdateAssignmentStatus = async (shiftId: string, driverId: string, status: AssignmentStatus) => {
    try {
      setAssignments((prev) =>
        prev.map((a) =>
          a.shiftId === shiftId && a.driverId === driverId
            ? { ...a, status, updatedAt: new Date().toISOString() }
            : a
        )
      );
      alert(`Assignment status updated to ${status.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update assignment status', error);
      alert('Failed to update assignment status. Please try again.');
    }
  };

  const handleShiftChange = (shiftId: string) => {
    setSelectedShiftId(shiftId);
    setSelectedDriverIds([]);
  };

  const getDriverStatusCounts = (shiftId: string) => {
    const assignedDriverIds = getAssignedDriversForShift(shiftId);
    const assignedDrivers = drivers.filter((d) => assignedDriverIds.includes(d.id));

    return {
      online: assignedDrivers.filter((d) => d.status === 'Online').length,
      busy: assignedDrivers.filter((d) => d.status === 'Busy').length,
      idle: assignedDrivers.filter((d) => d.status === 'Idle').length,
      total: assignedDrivers.length,
    };
  };

  const handleViewShiftDetails = (shiftId: string) => {
    navigate(`/shift-management/shifts/${shiftId}`);
  };

  const handleEditShift = (shiftId: string) => {
    navigate(`/shift-management/shifts/${shiftId}/edit`);
  };

  const handleCreateTemplate = () => {
    navigate('/shift-management/templates/create');
  };

  const handleViewTemplate = (templateId: string) => {
    navigate(`/shift-management/templates/${templateId}`);
  };

  const handleEditTemplate = (templateId: string) => {
    navigate(`/shift-management/templates/${templateId}/edit`);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    const formatErrorMessage = (errorMsg: string): string => {
      if (!errorMsg) return 'Failed to delete shift schedule. Please try again.';

      if (errorMsg.includes('ForeignKeyViolation') || errorMsg.includes('foreign key constraint')) {
        if (errorMsg.includes('shifts')) {
          return 'This shift schedule cannot be deleted because it is still being used by existing shift sessions. Please remove or reassign the shift sessions first.';
        }
        return 'This shift schedule cannot be deleted because it is still being referenced by other records.';
      }

      if (errorMsg.includes('PG::')) {
        const errorMatch = errorMsg.match(/ERROR:\s*(.+?)(?:\n|$)/);
        if (errorMatch && errorMatch[1]) {
          return errorMatch[1].trim();
        }
        return errorMsg.replace(/^PG::[^:]+:\s*/, '').replace(/\n.*$/, '').trim();
      }

      return errorMsg;
    };

    if (window.confirm('Are you sure you want to delete this shift schedule? This will not delete generated shift sessions.')) {
      try {
        const response = await deleteTemplateMutation({
          variables: {
            input: { id: templateId },
          },
        });

        if (response.errors && response.errors.length > 0) {
          const rawErrorMessage = response.errors[0]?.message || 'Failed to delete shift schedule';
          const errorMessage = formatErrorMessage(rawErrorMessage);
          toast.error(errorMessage);
          return;
        }

        if (!response.data) {
          toast.error('Unable to delete shift schedule. The server did not respond. Please try again.');
          return;
        }

        if (response.data.error) {
          const errorMessage = formatErrorMessage(response.data.error.message || 'Failed to delete shift schedule');
          toast.error(errorMessage);
          return;
        }

        if (!response.data.deleteShiftTemplate) {
          toast.error('Unable to delete shift schedule. The server response was invalid. Please try again.');
          return;
        }

        if (response.data.deleteShiftTemplate.success === true) {
          toast.success('Shift schedule deleted successfully');
          await refetchTemplates();
        } else {
          toast.error('Unable to delete shift schedule. The operation was not successful. Please try again.');
        }
      } catch (error: any) {
        if (error.networkError) {
          const networkErr = error.networkError;

          if (networkErr.statusCode || networkErr.result) {
            const resultError = networkErr.result?.error || networkErr.result?.errors?.[0];

            if (resultError) {
              const rawErrorMessage = resultError.message || networkErr.message || 'Unable to delete shift schedule. Please try again.';
              const errorMessage = formatErrorMessage(rawErrorMessage);
              toast.error(errorMessage);
            } else {
              const readableMessage = networkErr.message || 'Unable to delete shift schedule due to a network error. Please check your connection and try again.';
              toast.error(readableMessage);
            }
          } else if (networkErr.message === 'Failed to fetch' || networkErr.name === 'TypeError') {
            toast.error('Cannot connect to the server. Please check your internet connection and try again.');
          } else {
            const readableMessage = networkErr.message || 'A network error occurred while deleting the shift schedule. Please try again.';
            toast.error(readableMessage);
          }
        } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const rawErrorMessage = error.graphQLErrors[0].message || 'Unable to delete shift schedule. Please try again.';
          const errorMessage = formatErrorMessage(rawErrorMessage);
          toast.error(errorMessage);
        } else {
          const rawErrorMessage = error.message || 'Unable to delete shift schedule. An unexpected error occurred. Please try again.';
          const errorMessage = formatErrorMessage(rawErrorMessage);
          toast.error(errorMessage);
        }
      }
    }
  };

  const handleToggleTemplate = async (templateId: string) => {
    const template = templates.find((t: ShiftTemplate) => t.id === templateId);
    if (!template) return;

    const newStatus = template.status === 'active' ? 'inactive' : 'active';

    try {
      const response = await updateTemplateMutation({
        variables: {
          input: {
            id: templateId,
            status: newStatus.toUpperCase(),
          },
        },
      });

      if (response.data?.updateShiftTemplate?.shiftTemplate) {
        toast.success(`Shift schedule ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        await refetchTemplates();
      }
    } catch (error: any) {
      console.error('Failed to toggle template status', error);
      toast.error(error.message || 'Failed to toggle shift schedule status. Please try again.');
    }
  };

  const handleGenerateShifts = (templateId: string) => {
    navigate(`/shift-management/templates/${templateId}`);
  };

  const renderContent = () => {
    if (activeTab === 'reports') {
      return null;
    }

    switch (activeTab) {
      case 'shifts':
        return (
          <ShiftsView
            shifts={shifts}
            drivers={drivers}
            assignments={assignments}
            getAssignedDriversForShift={getAssignedDriversForShift}
            getDriverStatusCounts={getDriverStatusCounts}
            onToggleShift={handleToggleShift}
            onViewShiftDetails={handleViewShiftDetails}
            onEditShift={handleEditShift}
            onCreateShift={handleCreateShift}
            isShiftEnabled={isShiftEnabled}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            paginatedShifts={paginatedShifts}
            page={page}
            totalPages={totalPages}
            totalShifts={totalShiftsCount}
            onPageChange={setPage}
            loading={shiftsLoading || driversLoading}
            filters={shiftFilters}
            onFiltersChange={(newFilters) => setShiftFilters({
              cityId: newFilters.cityId,
              neighborhoodId: newFilters.neighborhoodId,
              status: newFilters.status as ShiftStatus | undefined,
              startDate: newFilters.startDate,
              endDate: newFilters.endDate,
              search: newFilters.search,
              order: newFilters.order || 'desc',
              shiftTemplateId: newFilters.shiftTemplateId,
              activeNow: newFilters.activeNow,
            })}
          />
        );
      case 'assignments':
        return (
          <DriverAssignmentView
            shifts={shifts}
            drivers={drivers}
            assignments={assignments}
            selectedShiftId={selectedShiftId}
            selectedDriverIds={selectedDriverIds}
            onShiftChange={handleShiftChange}
            onDriverToggle={handleDriverToggle}
            onAssignDrivers={handleAssignDrivers}
            onUnassignDriver={handleUnassignDriver}
            onUpdateAssignmentStatus={handleUpdateAssignmentStatus}
            getAssignedDriversForShift={getAssignedDriversForShift}
            getAvailableDrivers={getAvailableDrivers}
            onLoadMoreDrivers={loadMoreDrivers}
            hasMoreDrivers={hasMoreDrivers}
            driversLoading={driversLoading}
            driverSearchQuery={driverSearchQuery}
            onDriverSearchChange={setDriverSearchQuery}
            selectedShift={shifts.find(s => s.id === selectedShiftId)}
          />
        );
      case 'monitor':
        return (
          <LiveMonitorView
            drivers={drivers}
            assignments={assignments}
          />
        );
      case 'templates':
        return (
          <ShiftTemplatesView
            templates={templates}
            onCreateTemplate={handleCreateTemplate}
            onViewTemplate={handleViewTemplate}
            onEditTemplate={handleEditTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            onToggleTemplate={handleToggleTemplate}
            onGenerateShifts={handleGenerateShifts}
            viewMode={templateViewMode}
            onViewModeChange={setTemplateViewMode}
            page={templatePage}
            totalPages={Math.ceil((templatesData?.fetchAllShiftTemplates?.totalCount || templates.length) / ITEMS_PER_PAGE)}
            totalTemplates={templatesData?.fetchAllShiftTemplates?.totalCount || templates.length}
            onPageChange={setTemplatePage}
            ITEMS_PER_PAGE={ITEMS_PER_PAGE}
            loading={templatesLoading}
            filters={templateFilters}
            onFiltersChange={(newFilters) => setTemplateFilters({
              cityId: newFilters.cityId,
              status: newFilters.status,
              recurrenceType: newFilters.recurrenceType,
              search: newFilters.search,
            })}
          />
        );
      default:
        return (
          <ShiftsView
            shifts={shifts}
            drivers={drivers}
            assignments={assignments}
            getAssignedDriversForShift={getAssignedDriversForShift}
            getDriverStatusCounts={getDriverStatusCounts}
            onToggleShift={handleToggleShift}
            onViewShiftDetails={handleViewShiftDetails}
            onEditShift={handleEditShift}
            onCreateShift={handleCreateShift}
            isShiftEnabled={isShiftEnabled}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            paginatedShifts={paginatedShifts}
            page={page}
            totalPages={totalPages}
            totalShifts={totalShiftsCount}
            onPageChange={setPage}
            filters={shiftFilters}
            onFiltersChange={(newFilters) => setShiftFilters({
              cityId: newFilters.cityId,
              neighborhoodId: newFilters.neighborhoodId,
              status: newFilters.status as ShiftStatus | undefined,
              startDate: newFilters.startDate,
              endDate: newFilters.endDate,
              search: newFilters.search,
              order: newFilters.order || 'desc',
              shiftTemplateId: newFilters.shiftTemplateId,
              activeNow: newFilters.activeNow,
            })}
          />
        );
    }
  };

  return (
    <div className="flex flex-col space-y-2 relative bg-white">
      <div className="main-wrapper">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="pageTitle mb-1 text-xl font-bold">Shift Management</h2>
            <p className="text-sm text-gray-500">
              Manage shifts, assign drivers, monitor activity, and track compliance —{' '}
              <button
                onClick={() => setDocsOpen(true)}
                className="text-xs text-primaryColor hover:text-darkBlue underline underline-offset-2 font-medium transition-colors duration-200 cursor-pointer"
              >
                documented here
              </button>
              .
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ShiftManagementDocs showButton={false} openD={docsOpen} onClose={() => setDocsOpen(false)} />
            <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 shadow-inner" role="group">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`relative px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${activeTab === tab.id
                      ? 'bg-white text-primaryColor shadow-md transform scale-[1.02]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                      }`}
                    title={tab.label}
                    aria-pressed={activeTab === tab.id}
                  >
                    <Icon fontSize="small" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {renderContent()}

      {isReportsMounted && (
        <div className={activeTab === 'reports' ? '' : 'hidden'}>
          <ComplianceReportsView
            drivers={drivers}
          />
        </div>
      )}
    </div>
  );
};

export default ShiftManagement;
